import { Search } from 'lucide-react'
import type { ReactNode } from 'react'
import type { Requirement, RequirementTheme } from '@ispot/shared'
import { useRequirementsViewStore } from '@/stores/requirementsViewStore'
import { FilterButton } from './FilterButton'
import { GroupButton } from './GroupButton'
import { ActiveFilterChips } from './ActiveFilterChips'

interface Props {
  requirements: Requirement[]
  themes: RequirementTheme[]
  /** Hide the Group control in views that drive grouping elsewhere (Table). */
  hideGroup?: boolean
  /** Optional slot before the search box (e.g. a sidebar toggle). */
  leading?: ReactNode
  /** Optional slot for view-specific controls (e.g. Outline/Summary/Full toggle in Document). */
  trailing?: ReactNode
}

/**
 * Single horizontal toolbar shared across Tree, Document, and Table views.
 * Owns: search input, Filter button (popover), active filter chips below.
 *
 * Source of truth is the requirements view store — toggling here is reflected
 * in any other view that mounts a sidebar or another instance of this toolbar.
 */
export function ViewToolbar({ requirements, themes, hideGroup, leading, trailing }: Props) {
  const searchQuery = useRequirementsViewStore(s => s.searchQuery)
  const setSearchQuery = useRequirementsViewStore(s => s.setSearchQuery)

  return (
    <div
      className="flex flex-col flex-shrink-0 px-4 py-2"
      style={{
        borderBottom: '1px solid var(--color-warm-200)',
        background: 'var(--color-bg-card)',
      }}
    >
      <div className="flex items-center gap-2 flex-wrap">
        {leading}
        <div
          className="flex items-center gap-2"
          style={{
            width: 320,
            height: 32,
            padding: '0 10px',
            borderRadius: 8,
            background: 'var(--color-warm-100)',
            border: '1px solid transparent',
          }}
        >
          <Search className="h-4 w-4 flex-shrink-0" style={{ color: 'var(--color-fg-muted)' }} />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Filter by name, description, tag…"
            className="flex-1 bg-transparent outline-none border-0 min-w-0 text-sm"
            style={{
              color: 'var(--color-fg)',
              fontFamily: 'var(--font-body)',
            }}
          />
        </div>
        <FilterButton requirements={requirements} themes={themes} />
        {!hideGroup && <GroupButton />}
        <div className="flex-1" />
        {trailing}
      </div>
      <div className="mt-2">
        <ActiveFilterChips themes={themes} />
      </div>
    </div>
  )
}
